<template lang="html">
  <NoteShell class="note" @click.native="focusInput">
    <div class="sizer large" ref="sizerL"></div>
    <div class="sizer medium" ref="sizerM"></div>
    <div class="sizer small" ref="sizerS"></div>
    <AutoList v-if="type === 'bullet'" :disabled="!editable" ref="autolist" :value="value" @input="handleListInput" />
    <ContinuousTextarea v-if="type === 'text'" :class="[fontSize]" :disabled="!editable" :value="value" @input="handleTextareaInput"/>
    <footer>
      <span class="date" @click="activateDatePicker">{{ currentDate }}</span>
      <div class="add-emotion-button" ref="addEmotion" @click="showEmotionSelector">
        <Icon v-if="!emotion && editable" icon="add-emotion"/>
        <Emotion v-else-if="emotion" :emotion="emotion" />
      </div>
      <transition name="expand" @after-enter="setFaderThreshold">
        <div v-show="emotionSelector" class="emotion-selector" :class="{ 'fader-left': faderLeft, 'fader-right': faderRight }">
          <ul class="emotion-list" @scroll="handleEmotionListFaders">
            <li v-for="(availableEmotion, index) in Object.keys($store.state.application.emotions)" :key="index" @click="changeEmotion(availableEmotion)"><Emotion :class="{ active: emotion === availableEmotion}" :emotion="availableEmotion" /></li>
            <li @click="changeEmotion(null)"><Icon icon="cross" /></li>
          </ul>
        </div>
      </transition>
    </footer>
    <Modal :show="showDatePicker" @close="hideDatePicker" @after-leave="$el.style.removeProperty('will-change')">
      <header>
        <Button label="Previous Month" icon @click.native="moveMonth('backward')"><Icon icon="chevron-left" /></Button>
        <span class="label">{{pickerMonth}}</span>
        <Button label="Next Month" :disabled="!nextMonthAvailable" icon @click.native="moveMonth('forward')"><Icon icon="chevron-right" /></Button>
      </header>
      <section class="calendar">
        <header>
          <span class="label subdued">M</span>
          <span class="label subdued">T</span>
          <span class="label subdued">W</span>
          <span class="label subdued">T</span>
          <span class="label subdued">F</span>
          <span class="label subdued">S</span>
          <span class="label subdued">S</span>
        </header>
        <section class="days">
          <Button v-for="date in pickerDays" :class="{active: date.active}" :disabled="date.disabled" icon :key="date.rawDate.valueOf()" :label="`${date.month} ${date.day}`" @click.native="setPickerDate(date.rawDate)">{{date.day}}</Button>
        </section>
      </section>
      <section class="time">
        <span class="label">Time:</span>
        <input v-model="pickerTime" type="text" />
      </section>
      <Button class="accept-button" :disabled="pickerTimeInvalid" @click.native="acceptNewDate">Accept</Button>
    </Modal>
  </NoteShell>
</template>

<script>
import { Date as SugarDate } from 'sugar-date';

import AutoList from './AutoList.vue';
import Button from './Button.vue';
import ContinuousTextarea from './ContinuousTextarea.vue';
import Emotion from './Emotion.vue';
import Icon from './Icon.vue';
import Modal from './Modal.vue';
import NoteShell from './NoteShell.vue';

export default {
  components: {
    AutoList,
    Button,
    ContinuousTextarea,
    Emotion,
    Icon,
    Modal,
    NoteShell,
  },
  computed: {
    currentDate() {
      return new SugarDate(this.createdAt).format('{HH}:{mm}, {dd}. {Month} {yyyy}');
    },
    nextMonthAvailable() {
      return !SugarDate.isFuture(SugarDate.addMonths(new Date(this.pickerDate), 1, true));
    },
    pickerMonth() {
      return new SugarDate(this.pickerDate).format('{Month} {yyyy}');
    },
    pickerDays() {
      const date = new SugarDate(this.pickerDate);
      const startOfMonth = date.clone().beginningOfMonth().raw;
      const endOfMonth = date.clone().endOfMonth().raw;
      const start = date.clone().beginningOfMonth().beginningOfISOWeek().raw;
      const end = date.clone().endOfMonth().endOfISOWeek().raw;
      const rawDays = SugarDate.range(start, end).every('day');
      const cleanDays = [];
      rawDays.forEach((day) => {
        cleanDays.push({
          active: SugarDate.is(day, SugarDate.beginningOfDay(new Date(this.pickerDate))),
          day: SugarDate.format(day, '{d}'),
          disabled: SugarDate.isFuture(day) || SugarDate.isBefore(day, startOfMonth) || SugarDate.isAfter(day, endOfMonth),
          month: SugarDate.format(day, '{Month}'),
          rawDate: day,
        });
      });
      return cleanDays;
    },
    pickerTimeInvalid() {
      if (this.pickerTime.length !== 5) return true;
      if (this.pickerTime.includes(':')) {
        const partials = this.pickerTime.split(':');

        if (Number.isNaN(parseInt(partials[0], 10)) || Number.isNaN(parseInt(partials[1], 10))) return true;
      }

      return false;
    },
  },
  created() {
    if (this.$route.query.datePicker) this.showDatePicker = true;
    this.pickerDate = new Date(this.createdAt);
    this.pickerTime = SugarDate.format(new Date(this.createdAt), '{HH}:{mm}');
  },
  data() {
    return {
      emotionSelector: false,
      faderLeft: false,
      faderRight: true,
      faderThreshold: 0,
      fontSize: 'large',
      pickerDate: null,
      pickerTime: '',
      showDatePicker: false,
      userWarned: false,
    };
  },
  methods: {
    acceptNewDate() {
      if (!this.pickerTimeInvalid) {
        let newDate = SugarDate.set(new Date(this.pickerDate), { hours: this.pickerTime.substr(0, 2), minutes: this.pickerTime.substr(3) }, true);
        if (SugarDate.isFuture(newDate)) newDate = SugarDate.set(newDate, { hours: SugarDate.format(new Date(this.createdAt), '{HH}'), minutes: SugarDate.format(new Date(this.createdAt), '{mm}') });
        this.$emit('date-change', newDate.valueOf());
      }
      this.hideDatePicker();
    },
    activateDatePicker() {
      if (!this.editable) return;
      const query = { ...this.$route.query, datePicker: true };
      this.$router.push({ query });
      this.$el.style.willChange = 'auto'; // so the modal doesn’t get stuck
    },
    changeEmotion(emotion) {
      this.$emit('emotion-change', emotion);
      this.hideEmotionSelector(true);
    },
    focusInput(e) {
      if (!this.editable || this.type !== 'bullet' || !this.$refs.autolist.$el.contains(e.target) || e.target.tagName.toLowerCase() === 'textarea') return;
      const inputs = this.$el.querySelectorAll('textarea');
      inputs[inputs.length - 1].focus();
    },
    handleEmotionListFaders(e) {
      if (e.target.scrollLeft === 0) this.faderLeft = false;
      else this.faderLeft = true;
      if (e.target.scrollLeft === this.faderThreshold) this.faderRight = false;
      else this.faderRight = true;
    },
    handleFontSize(value) {
      this.$refs.sizerL.innerText = value;
      this.$refs.sizerL.appendChild(document.createElement('BR'));
      this.$refs.sizerM.innerText = value;
      this.$refs.sizerM.appendChild(document.createElement('BR'));
      this.$refs.sizerS.innerText = value;
      this.$refs.sizerS.appendChild(document.createElement('BR'));

      const largeOverflow = this.$refs.sizerL.scrollHeight > this.$refs.sizerL.offsetHeight;
      const mediumOverflow = this.$refs.sizerM.scrollHeight > this.$refs.sizerM.offsetHeight;
      const smallOverflow = this.$refs.sizerS.scrollHeight > this.$refs.sizerS.offsetHeight;

      if (largeOverflow) this.fontSize = 'medium';
      if (mediumOverflow) this.fontSize = 'small';
      if (smallOverflow) this.fontSize = 'extra-small';
      if (!largeOverflow) this.fontSize = 'large';

      this.$emit('fontsize-change', this.fontSize);
    },
    handleListInput(value) {
      this.$emit('input', value);
      if (!this.userWarned && this.$refs.autolist.$el.scrollHeight > this.$refs.autolist.$el.offsetHeight) {
        this.$store.commit('addToast', { message: 'Your note doesn’t fit onto the sheet, please be aware that the lower parts will be cut off during photo-export', timeout: 10000 });
        this.userWarned = true;
      }

      if (this.userWarned && this.$refs.autolist.$el.scrollHeight === this.$refs.autolist.$el.offsetHeight) this.userWarned = false;
    },
    handleTextareaInput(value) {
      this.handleFontSize(value);

      this.$emit('input', value);
    },
    hideDatePicker() {
      const query = { ...this.$route.query };
      delete query.datePicker;
      this.$router.replace({ query });
    },
    hideEmotionSelector(event, force) {
      if (force || (event.target !== this.$refs.addEmotion && !this.$refs.addEmotion.contains(event.target) && !this.$el.querySelector('.emotion-selector').contains(event.target))) {
        this.emotionSelector = false;
        window.removeEventListener('click', this.hideEmotionSelector);
        window.removeEventListener('touchend', this.hideEmotionSelector);
      }
    },
    moveMonth(direction) {
      if (direction === 'forward' && this.nextMonthAvailable) this.pickerDate = SugarDate.addMonths(new Date(this.pickerDate), 1, true);
      else this.pickerDate = SugarDate.addMonths(new Date(this.pickerDate), -1, true);
    },
    setFaderThreshold() {
      const emotionList = this.$el.querySelector('.emotion-list');
      this.faderThreshold = emotionList.scrollWidth - emotionList.offsetWidth;
    },
    setPickerDate(date) {
      this.pickerDate = date;
    },
    showEmotionSelector() {
      if (!this.editable) return;
      this.emotionSelector = true;
      window.addEventListener('click', this.hideEmotionSelector);
      window.addEventListener('touchend', this.hideEmotionSelector);
    },
  },
  mounted() {
    if (this.value && this.value.length > 0) {
      this.handleFontSize(this.value);
    }
  },
  props: {
    createdAt: Number,
    editable: Boolean,
    emotion: String,
    type: {
      type: String,
      validator(v) {
        return ['bullet', 'text'].includes(v);
      },
    },
    value: [String, Array],
  },
  watch: {
    $route(newVal) {
      if (newVal.query.datePicker && !this.showDatePicker) this.showDatePicker = true;
      if (!newVal.query.datePicker && this.showDatePicker) this.showDatePicker = false;
    },
    editable(newVal) {
      if (newVal) {
        this.$nextTick(() => this.$el.querySelector('textarea').focus());
      }
    },
    createdAt(newVal) {
      this.pickerDate = new Date(newVal);
      this.pickerTime = SugarDate.format(new Date(newVal), '{HH}:{mm}');
    },
    value(newVal) {
      if (typeof newVal === 'string' && newVal.length > 0) this.handleFontSize(newVal);
    },
  },
};
</script>

<style lang="stylus" scoped>
@require '../styles/colors'

.note
  >>> .content
    display: flex
    flex-direction: column

    .sizer
      font-size: 2rem
      font-weight: 600
      line-height: 1.35
      white-space: pre-wrap
      word-wrap: break-word
      position: absolute
      width: calc(100% - 32px)
      height: calc(100% - 28px)
      overflow: hidden
      visibility: hidden

    .continuous-textarea,
    .auto-list
      height: calc(100% - 28px)

    .large
      font-size: 2rem

    .medium
      font-size: 1.5rem

    .small
      font-size: 1.1rem

    .extra-small
      font-size: 0.84rem

    footer
      margin-top: auto
      height: 20px
      font-weight: normal
      font-size: 0.75rem
      display: flex
      align-items: center
      justify-content: space-between
      position: relative

      .date
        opacity: 0.54
        flex-shrink: 0

      .add-emotion-button
        flex-shrink: 0
        width: 20px
        height: 20px
        display: flex
        justify-content: center
        align-items: center

      .emotion-selector
        position: absolute
        top: 0
        bottom: @top
        right: @top
        left: @top
        background-color: white

        &.expand-enter-active,
        &.expand-leave-active
          // transform-origin: right
          // transition: opacity 200ms ease, transform 200ms ease
          transition: left 200ms ease
          overflow: hidden
          border-radius: 10px

          &.expand-enter,
          &.expand-leave-to
            // transform: scaleX(0)
            // opacity: 0
            left: 100%

        .emotion-list
          padding: 0
          margin: 0
          list-style: none
          overflow-x: auto
          white-space: nowrap

          li
            display: inline-block

            &:not(:last-child)
              margin-right: 8px

            .icon
              margin: 1px

            // .emotion
            //   // opacity: 0.5
            //   transition: opacity 200ms ease
            //
            //   &.active
            //     opacity: 1

        &.fader-left::before,
        &.fader-right::after
          opacity: 1

        &::before,
        &::after
          content: ''
          position: absolute
          top: 0
          bottom: @top
          width: 32px
          z-index: 1
          opacity: 0
          transition: opacity 200ms ease
          pointer-events: none

        &::before
          left: 0
          background-image: linear-gradient(to right, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))

        &::after
          right: 0
          background-image: linear-gradient(to left, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))

  .modal
    >>> .card
      padding: 18px

    header
      display: flex
      align-items: center
      margin-bottom: 16px

      .button:first-child
        margin-right: auto
        margin-left: 0

      .button:last-child
        margin-left: auto
        margin-right: 0

    .calendar
      width: 324px
      margin: 0 auto

      header
        margin: 24px -6px 6px -6px
        .label
          margin: 0 auto

      .days
        margin: 0 -6px
        display: flex
        flex-wrap: wrap

        button
          margin: 6px
          transition: background-color 200ms ease, color 200ms ease

          &.active
            color: $text
            background-color: $selection

          &.disabled
            color: $text
            background-color: transparent

    .time
      margin-top: 24px
      margin-bottom: 32px
      display: flex
      align-items: center

      .label
        margin-right: auto

      input
        text-align: center
        width: 86px

    .accept-button
      display: block
      width: 160px

    .label
      text-transform: uppercase
      font-weight: 600
      letter-spacing: 1px

      &.subdued
        color: alpha($text, 0.5)
</style>
